import { normalizePublicConfig } from './config';
import { renderContactCapture } from './renderer';
import type { PublicConfig } from './types';

/** Runs only in the builder's sandboxed frame; shares the live renderer without API requests. */
export const startBuilderPreview = () => {
  let config: PublicConfig | undefined;
  let previewState: 'form' | 'success' = 'form';
  let dispose: (() => void) | undefined;
  let reopenTimer: number | undefined;
  const post = (message: Record<string, unknown>) => window.parent.postMessage(message, '*');

  const render = () => {
    if (!config) return;
    window.clearTimeout(reopenTimer);
    dispose?.();
    const host = document.createElement('div');
    host.setAttribute('data-bhi-preview', 'true');
    document.body.appendChild(host);
    dispose = renderContactCapture({
      captureId: config.id,
      configUrl: 'data:application/json,',
      mode: config.type,
      host,
      previewState,
    }, config);
  };

  const onMessage = (event: MessageEvent) => {
    if (event.source !== window.parent || event.data?.type !== 'bhi-preview:config') return;
    try {
      config = normalizePublicConfig(event.data.config);
      previewState = event.data.previewState === 'success' ? 'success' : 'form';
      render();
      // Also acknowledge config messages if the initial ready message preceded React's effect.
      post({ type: 'bhi-preview:ready' });
    } catch (error) {
      post({ type: 'bhi-preview:error', message: error instanceof Error ? error.message : 'Invalid preview config.' });
    }
  };
  const onClosed = () => { reopenTimer = window.setTimeout(render, 400); };
  window.addEventListener('message', onMessage);
  window.addEventListener('bhi:capture:closed', onClosed);
  post({ type: 'bhi-preview:ready' });

  return () => {
    window.removeEventListener('message', onMessage);
    window.removeEventListener('bhi:capture:closed', onClosed);
    window.clearTimeout(reopenTimer);
    dispose?.();
  };
};
